export default function PaymentConfirmationModule() {
  return (
    <section id="payment" className="py-24 px-6 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: Flow mockup */}
          <div className="order-2 lg:order-1 space-y-3">
            {/* Step 1: Acceptance alert */}
            <div className="bg-white rounded-xl border border-teal-100 p-4 flex items-start gap-3">
              <div className="w-9 h-9 bg-teal-50 rounded-lg flex items-center justify-center shrink-0">
                <i className="ri-checkbox-circle-fill text-teal-500 text-lg" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-0.5">
                  <p className="text-xs font-semibold text-gray-800">Quote Accepted!</p>
                  <span className="text-[10px] bg-teal-50 text-teal-600 px-2 py-0.5 rounded-full">Just now</span>
                </div>
                <p className="text-xs text-gray-500">Sara Al-Farsi accepted Quote #QT-2024-0089 — Bali Romantic Escape ($2,590)</p>
              </div>
            </div>

            {/* Step 2: Agent sends payment link */}
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <p className="text-xs font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <i className="ri-secure-payment-line text-teal-500" />
                Agent Sends Payment Link
              </p>
              <div className="bg-gray-50 rounded-lg px-3 py-2 flex items-center gap-2 mb-3">
                <i className="ri-link text-gray-400 text-sm" />
                <span className="text-xs text-gray-500 flex-1">https://pay.stripe.com/your-agency/...</span>
                <button type="button" className="text-[10px] bg-teal-100 text-teal-700 px-2 py-0.5 rounded cursor-pointer whitespace-nowrap hover:bg-teal-200 transition-colors">Paste</button>
              </div>
              <div className="flex gap-2">
                <button type="button" className="flex-1 text-[11px] font-medium bg-[#075E54] text-white py-2 rounded-lg flex items-center justify-center gap-1 cursor-pointer hover:bg-[#064e46] transition-colors whitespace-nowrap">
                  <i className="ri-whatsapp-line" />Send via WhatsApp
                </button>
                <button type="button" className="flex-1 text-[11px] font-medium bg-gray-100 text-gray-700 py-2 rounded-lg flex items-center justify-center gap-1 cursor-pointer hover:bg-gray-200 transition-colors whitespace-nowrap">
                  <i className="ri-mail-line" />Send via Email
                </button>
              </div>
            </div>

            {/* Step 3: Confirmation sent */}
            <div className="bg-white rounded-xl border border-emerald-100 p-4">
              <p className="text-xs font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <i className="ri-send-plane-fill text-emerald-500" />
                Booking Confirmation Sent
              </p>
              <div className="space-y-2">
                {[
                  { channel: 'WhatsApp', status: 'Delivered ✓✓', icon: 'ri-whatsapp-line', color: 'text-[#075E54]' },
                  { channel: 'Email', status: 'Delivered ✓', icon: 'ri-mail-check-line', color: 'text-teal-600' },
                ].map((c) => (
                  <div key={c.channel} className="flex items-center justify-between bg-emerald-50 rounded-lg px-3 py-2">
                    <div className="flex items-center gap-2">
                      <i className={`${c.icon} ${c.color} text-sm`} />
                      <p className="text-xs font-medium text-gray-700">{c.channel} Confirmation</p>
                    </div>
                    <span className="text-[10px] text-emerald-600 font-medium">{c.status}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 bg-gray-50 rounded-lg px-3 py-2">
                <p className="text-[10px] text-gray-500 italic">&ldquo;Your Bali trip is confirmed! Booking Ref: TW-2024-0089. We&rsquo;ll be in touch with your travel documents. Thank you for choosing us! ✈️&rdquo;</p>
              </div>
            </div>
          </div>

          {/* Right: Content */}
          <div className="order-1 lg:order-2">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 flex items-center justify-center bg-teal-50 rounded-lg">
                <i className="ri-bank-card-line text-teal-500" />
              </div>
              <span className="text-xs font-semibold uppercase tracking-widest text-teal-600">Module 06</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-semibold text-gray-900 leading-tight tracking-tight mb-4">
              Payment Link & <br />
              <span className="bg-gradient-to-r from-teal-500 to-emerald-500 bg-clip-text text-transparent">Booking Confirmation</span>
            </h2>
            <p className="text-gray-500 text-base leading-relaxed mb-6">
              Once a customer accepts a quote, your agent pastes in a payment link — from any processor you already use. TreWaz sends it via WhatsApp or email. The moment the booking is confirmed, TreWaz fires off a beautiful confirmation message.
            </p>
            <ul className="flex flex-col gap-4">
              {[
                { icon: 'ri-notification-3-line', title: 'Instant acceptance alert', desc: 'The second a customer taps "Accept", you get a notification in your dashboard and via WhatsApp.' },
                { icon: 'ri-links-line', title: 'Use your own payment link', desc: 'TreWaz doesn\'t process payments — just helps you send the link. Use Stripe, bank transfer, or anything you like.' },
                { icon: 'ri-toggle-line', title: 'Choose your confirmation channel', desc: 'Decide per booking whether to send the confirmation via WhatsApp, email, or both. Your agency, your workflow.' },
                { icon: 'ri-file-shield-2-line', title: 'Booking record auto-created', desc: 'Once confirmed, the booking is logged in your CRM with full customer details, trip summary, and payment status.' },
              ].map((item) => (
                <li key={item.title} className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-teal-50 rounded-lg shrink-0 mt-0.5">
                    <i className={`${item.icon} text-teal-600 text-sm`} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800 mb-0.5">{item.title}</p>
                    <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
